// client/src/components/pages/LexiStar/Reports/RunReport.tsx
//
// /lexistar/reports/run/:run_id — the run summary (ST-00188 M4-R2; FR-RPT-008;
// LS-09 §5.24 "at run end the strip becomes the run summary report"). One
// truck's shift or day: the chain of trips in order with sites, materials and
// tonnes per load; loaded km, empty km and empty-running %; litres against the
// allowance per leg and in total with over-consumption flags; turn-around time
// per site split into offloading, waiting and loading; deviations and their
// outcomes. Sent to the configured recipients at run end (M4-R1 renders this
// page with ?print=1).
//
// Data: GET /runs/:id/summary (run, trips with legs, turnarounds, totals — the
// engine's run roll-ups win over the computed ones) and, per trip,
// GET /deviations?trip_id=&state=<every state> for the outcomes.

import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaDesktop, FaPrint } from 'react-icons/fa';
import { getRunSummary, listDeviations } from '../../../../services/lexistarTrips';
import { STAGE_LABELS } from '../StatusBoard/statusLanguage';
import { fmtDateTime, fmtKm, fmtL, fmtNum, fmtTime } from '../StatusBoard/format';
import { formatMiningDateLong } from '../../../../utils/dateUtils';
import { errorText, pickList, statusOf, isObj } from '../../../../services/lexistarReports';
import { kindLabel, numOrNull } from './reportLogic';
import {
  ExportButtons,
  How,
  PaperMessage,
  Prov,
  ProvenanceKey,
  ReportDoc,
  ReportFrame,
  ReportRenderContext,
  Section,
  toolbarButton,
  toolbarPrimary,
  usePrintMode,
  type LoadState,
} from './reportAtoms';
import { useAuth } from '../../../../types/commonComponents';
import type { Loose, TripLeg } from '../../../../types/lexistar-reports';

const ALL_DEVIATION_STATES = ['SUSPECTED', 'OPEN', 'AWAITING_DECISION', 'APPROVED', 'REJECTED', 'ESCALATED', 'CLOSED', 'VOIDED'];

interface RunTrip extends Loose {
  id: string;
  reference?: string;
  seq?: number;
  stage?: string;
  origin?: string;
  from_name?: string | null;
  to_name?: string | null;
  material?: string | null;
  planned_tonnes?: number | null;
  net_tonnes?: number | null;
  budget_l?: number | null;
  consumed_l?: number | null;
  provenance?: string | null;
  over_consumption?: { excess_l: number; severity: string; certainty: string } | null;
  flags?: string[];
  legs?: TripLeg[];
}

interface RunData {
  run: Loose;
  trips: RunTrip[];
  turnarounds: Loose[];
  totals: Loose;
  deviations: Map<string, Loose[]>;
  deviationsState: 'ok' | 'unavailable';
}

async function loadRun(runId: string): Promise<RunData> {
  const body = (await getRunSummary(runId)) as Loose;
  const trips = pickList<RunTrip>(body, 'trips').sort((a, b) => (numOrNull(a.seq) ?? 0) - (numOrNull(b.seq) ?? 0));
  const results = await Promise.allSettled(
    trips.map((t) => listDeviations({ trip_id: t.id, state: ALL_DEVIATION_STATES, limit: 100 }))
  );
  const deviations = new Map<string, Loose[]>();
  results.forEach((r, i) => {
    if (r.status === 'fulfilled') deviations.set(trips[i]!.id, pickList<Loose>(r.value, 'deviations'));
  });
  return {
    run: isObj(body.run) ? body.run : {},
    trips,
    turnarounds: pickList<Loose>(body, 'turnarounds'),
    totals: isObj(body.totals) ? body.totals : {},
    deviations,
    deviationsState: results.some((r) => r.status === 'rejected') ? 'unavailable' : 'ok',
  };
}

const circled = (n: number) => (n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + n - 1) : `#${n}`);
const minutes = (v: unknown) => (numOrNull(v) === null ? '—' : `${Math.round(Number(v))} min`);
const pct = (ratio: unknown) => (numOrNull(ratio) === null ? '—' : `${(Number(ratio) * 100).toFixed(1)}%`);

function tonnesOf(t: RunTrip): { value: number | null; planned: boolean } {
  if (numOrNull(t.net_tonnes) !== null) return { value: Number(t.net_tonnes), planned: false };
  if (numOrNull(t.planned_tonnes) !== null) return { value: Number(t.planned_tonnes), planned: true };
  return { value: null, planned: false };
}

function OverTag({ oc }: { oc: RunTrip['over_consumption'] }) {
  if (!oc) return null;
  return (
    <span className="lxr-tag lxr-tag--attention" title={`${oc.severity} · ${oc.certainty}`}>
      +{fmtNum(oc.excess_l, 1)} L {oc.certainty}
    </span>
  );
}

function RunDocument({ data, print, generatedAt }: { data: RunData; print: boolean; generatedAt: string | null }) {
  const { run, trips, turnarounds, totals } = data;
  const live = trips.filter((t) => !['CANCELLED', 'ABORTED'].includes(String(t.stage)));
  const registration = String(run.registration || String(run.asset_key || '').replace(/^(reg|asset):/, '') || '—');
  const used = numOrNull(totals.litres_used);
  const allowance = numOrNull(totals.litres_allowance);
  const over = used !== null && allowance !== null && used > allowance;
  const loadedKm = numOrNull(totals.loaded_km);
  const emptyKm = numOrNull(totals.empty_km);
  const open = String(run.state) !== 'closed';
  const allDeviations = trips.flatMap((t) => (data.deviations.get(t.id) || []).map((d) => ({ trip: t, d })));

  return (
    <ReportDoc
      print={print}
      label={`Run summary ${registration}`}
      reportName="Run summary"
      title={`Run · ${registration}`}
      subtitle={
        <>
          {formatMiningDateLong(run.shift_date ? `${String(run.shift_date)}T12:00:00+02:00` : null) || String(run.shift_date || '—')}
          {' · '}
          {String(run.driver_name || run.driver_tag || 'No driver identified')}
        </>
      }
      chips={
        <>
          <span className={`lxr-chip ${open ? 'lxr-chip--dashed' : 'lxr-chip--ink'}`}>{open ? 'Run open' : 'Run closed'}</span>
          {open && <span className="lxr-chip lxr-chip--dashed">Provisional</span>}
        </>
      }
      generatedAt={generatedAt}
      footer={
        <>
          <span>
            Run {String(run.id || '').slice(0, 8)} · <ProvenanceKey /> · km from the pinned plan legs; the engine’s run roll-ups win where present
          </span>
          <span>{open ? 'Provisional — the run is still open' : 'Final'}</span>
        </>
      }
    >
      <dl className="lxr-kpis" aria-label="Run totals">
        <div className="lxr-kpi">
          <dt>Loads</dt>
          <dd>
            {fmtNum(totals.loads)}
            <small>{fmtNum(live.length)} trips</small>
          </dd>
        </div>
        <div className="lxr-kpi">
          <dt>Tonnes</dt>
          <dd>
            {fmtNum(totals.tonnes, 1)} t<small>receipt, else dispatch / plan</small>
          </dd>
        </div>
        <div className="lxr-kpi">
          <dt>Loaded km</dt>
          <dd>{fmtKm(loadedKm)}</dd>
        </div>
        <div className="lxr-kpi">
          <dt>Empty km</dt>
          <dd>{fmtKm(emptyKm)}</dd>
        </div>
        <div className="lxr-kpi">
          <dt>Empty running</dt>
          <dd>{pct(totals.empty_running_pct)}</dd>
        </div>
        <div className="lxr-kpi">
          <dt>Litres</dt>
          <dd className={over ? 'lxr-alert' : undefined}>
            {fmtNum(used)} / {fmtL(allowance)}
            <small>{over ? `▲ ${fmtNum((used ?? 0) - (allowance ?? 0))} L over the allowance` : 'within the allowance'}</small>
          </dd>
        </div>
      </dl>

      <div className="lxr-stack">
        <Section title="The day" aside="trips in order, turn-around at the sites between them">
          {trips.length === 0 ? (
            <p className="lxr-empty">No trips on this run.</p>
          ) : (
            <ol className="lxr-strip" aria-label="Run strip">
              {trips.flatMap((t, i) => {
                const tonnes = tonnesOf(t);
                const items = [
                  <li key={t.id} className={`lxr-strip-trip ${t.origin === 'prompted' ? 'lxr-strip-trip--prompted' : ''}`} aria-label={`Trip ${String(t.seq)}: ${String(t.from_name)} to ${String(t.to_name)}`}>
                    <strong>
                      {circled(numOrNull(t.seq) ?? i + 1)} {String(t.from_name || '—')} → {String(t.to_name || '—')}
                    </strong>
                    <span className="lxr-muted">
                      {String(t.reference || '')}
                      {t.material ? ` · ${t.material}` : ''}
                    </span>
                    <br />
                    {tonnes.value !== null ? `${fmtNum(tonnes.value, 1)} t${tonnes.planned ? ' planned' : ''}` : '—'} · {fmtL(t.consumed_l)} / {fmtL(t.budget_l)}
                    {t.over_consumption && <span className="lxr-alert"> ▲</span>}
                  </li>,
                ];
                const ta = turnarounds.find((b) => numOrNull(b.after_seq) === numOrNull(t.seq));
                if (ta && i < trips.length - 1) {
                  items.push(
                    <li key={`ta-${String(t.seq)}`} className="lxr-strip-turn" aria-label={`Turn-around at ${String(ta.site_name)}`}>
                      <strong style={{ display: 'block' }}>{String(ta.site_name || '—')}</strong>
                      {[
                        ['off', ta.offloading_min],
                        ['wait', ta.waiting_min],
                        ['load', ta.loading_min],
                      ]
                        .filter(([, v]) => numOrNull(v) !== null)
                        .map(([k, v]) => `${String(k)} ${Math.round(Number(v))}m`)
                        .join(' · ') || 'turn-around'}
                    </li>
                  );
                }
                return items;
              })}
            </ol>
          )}
        </Section>

        <Section title="Trips" aside={<span>tonnes: ● receipt / dispatch ticket · planned when not weighed</span>}>
          <div className="lxr-scroll">
            <table className="lxr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Trip</th>
                  <th>From → to</th>
                  <th>Material</th>
                  <th className="lxr-num">Tonnes</th>
                  <th className="lxr-num">Litres / allowance</th>
                  <th>Stage</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t, i) => {
                  const tonnes = tonnesOf(t);
                  return (
                    <tr key={t.id}>
                      <td>{numOrNull(t.seq) ?? i + 1}</td>
                      <td>
                        {print ? String(t.reference || '') : <Link to={`/lexistar/reports/trip/${t.id}`}>{String(t.reference || '')}</Link>}
                        {t.origin === 'prompted' && <small>retrospective</small>}
                      </td>
                      <td>
                        {String(t.from_name || '—')} → {String(t.to_name || '—')}
                      </td>
                      <td>{t.material || '—'}</td>
                      <td className="lxr-num">
                        {tonnes.value !== null ? `${fmtNum(tonnes.value, 1)} t` : '—'}
                        {tonnes.planned && <small>planned</small>}
                      </td>
                      <td className="lxr-num">
                        {fmtNum(t.consumed_l)}
                        <Prov provenance={t.provenance} /> / {fmtL(t.budget_l)}
                        {t.over_consumption && (
                          <div>
                            <OverTag oc={t.over_consumption} />
                          </div>
                        )}
                      </td>
                      <td>{STAGE_LABELS[String(t.stage) as keyof typeof STAGE_LABELS] || String(t.stage || '—')}</td>
                      <td>{(t.flags || []).map((f) => kindLabel(f).toLowerCase()).join(', ') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Total</td>
                  <td className="lxr-num">{fmtNum(totals.tonnes, 1)} t</td>
                  <td className="lxr-num">
                    {fmtNum(used)} / {fmtL(allowance)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </Section>

        <Section title="Litres against the allowance, per leg">
          <div className="lxr-scroll">
            <table className="lxr-table">
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Leg</th>
                  <th>From → to</th>
                  <th className="lxr-num">Planned</th>
                  <th className="lxr-num">Allowance</th>
                  <th className="lxr-num">Used</th>
                  <th>Over-consumption</th>
                </tr>
              </thead>
              <tbody>
                {trips.flatMap((t) =>
                  (t.legs || [])
                    .filter((l) => l.state !== 'replaced')
                    .map((l) => (
                      <tr key={`${t.id}-${String(l.seq)}`}>
                        <td>{numOrNull(t.seq) ?? '—'}</td>
                        <td>
                          {kindLabel(l.kind)} <span className="lxr-muted">({l.load_state})</span>
                        </td>
                        <td>
                          {String(l.from_name || '—')} → {String(l.to_name || '—')}
                        </td>
                        <td className="lxr-num">{fmtKm(l.planned_km)}</td>
                        <td className="lxr-num">{fmtL(l.budget_l)}</td>
                        <td className="lxr-num">
                          {fmtL(l.consumed_l)}
                          <Prov provenance={l.consumed_provenance} />
                        </td>
                        <td>{l.over_consumption ? <OverTag oc={l.over_consumption} /> : <span className="lxr-muted">—</span>}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
          <How>
            <div>
              empty running = empty km ÷ (loaded km + empty km) = {fmtNum(emptyKm)} ÷ ({fmtNum(loadedKm)} + {fmtNum(emptyKm)}) ={' '}
              {pct(totals.empty_running_pct)}
            </div>
            <div>km are the pinned plan legs’ planned km (replaced legs excluded)</div>
            <div>
              litres = Σ trips’ consumed litres against Σ adjusted allowances = {fmtNum(used)} / {fmtNum(allowance)} L
            </div>
            <div>over-consumption: used − allowance &gt; max(3%, 2 L) per leg; confirmed only when measured — it never blocks</div>
          </How>
        </Section>

        <Section title="Turn-around at sites">
          {turnarounds.length === 0 ? (
            <p className="lxr-empty">No turn-arounds recorded yet.</p>
          ) : (
            <table className="lxr-table">
              <thead>
                <tr>
                  <th>After trip</th>
                  <th>Site</th>
                  <th className="lxr-num">Offloading</th>
                  <th className="lxr-num">Waiting</th>
                  <th className="lxr-num">Loading</th>
                  <th className="lxr-num">Total</th>
                </tr>
              </thead>
              <tbody>
                {turnarounds.map((b, i) => {
                  const parts = [b.offloading_min, b.waiting_min, b.loading_min].map(numOrNull);
                  const total = parts.every((p) => p === null) ? null : parts.reduce<number>((a, p) => a + (p ?? 0), 0);
                  return (
                    <tr key={`ta${i}`}>
                      <td>{numOrNull(b.after_seq) ?? '—'}</td>
                      <td>{String(b.site_name || '—')}</td>
                      <td className="lxr-num">{minutes(b.offloading_min)}</td>
                      <td className="lxr-num">{minutes(b.waiting_min)}</td>
                      <td className="lxr-num">{minutes(b.loading_min)}</td>
                      <td className="lxr-num lxr-strong">{minutes(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Deviations and their outcomes">
          {data.deviationsState !== 'ok' && allDeviations.length === 0 ? (
            <p className="lxr-empty">Not available from the server yet.</p>
          ) : allDeviations.length === 0 ? (
            <p className="lxr-empty">No deviations on this run.</p>
          ) : (
            <table className="lxr-table">
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Opened</th>
                  <th className="lxr-num">Off route</th>
                  <th>Reason</th>
                  <th>Outcome</th>
                  <th>Decided</th>
                </tr>
              </thead>
              <tbody>
                {allDeviations.map(({ trip, d }) => {
                  const state = String(d.state || 'OPEN');
                  const attention = !['APPROVED', 'CLOSED', 'VOIDED'].includes(state);
                  return (
                    <tr key={String(d.id)}>
                      <td>{numOrNull(trip.seq) ?? '—'}</td>
                      <td>
                        {fmtTime(d.opened_at)}–{d.closed_at ? fmtTime(d.closed_at) : 'open'}
                      </td>
                      <td className="lxr-num">{fmtKm(d.off_route_km, 1)}</td>
                      <td>{kindLabel(d.reason_code || (isObj(d.driver_reason) ? d.driver_reason.code : null)) || '—'}</td>
                      <td>
                        <span className={`lxr-tag ${attention ? 'lxr-tag--attention' : ''}`}>{kindLabel(state).toLowerCase()}</span>
                        {numOrNull(d.extra_km_approved) !== null && <small>+{fmtNum(d.extra_km_approved)} km approved</small>}
                      </td>
                      <td>
                        {d.decided_by ? String(d.decided_by) : '—'}
                        {d.decided_at ? <small>{fmtDateTime(d.decided_at)}</small> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </ReportDoc>
  );
}

export default function RunReport() {
  const { run_id: runId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const print = usePrintMode();
  const rendering = useContext(ReportRenderContext);
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.reports.view');
  const [state, setState] = useState<{ load: LoadState; data: RunData | null; error: string | null; at: string | null }>({
    load: 'loading',
    data: null,
    error: null,
    at: null,
  });

  const load = useCallback(async () => {
    if (!canView || !runId) return;
    try {
      const data = await loadRun(runId);
      setState({ load: 'ready', data, error: null, at: new Date().toISOString() });
    } catch (err) {
      setState({
        load: 'error',
        data: null,
        at: null,
        error: statusOf(err) === 404 ? 'Run not found, or not visible to your organisation.' : errorText(err, 'The run summary could not be loaded.'),
      });
    }
  }, [canView, runId]);

  useEffect(() => {
    load();
  }, [load]);

  const setPrint = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('print', '1');
    else next.delete('print');
    setParams(next, { replace: true });
  };

  if (!canView) {
    return (
      <ReportFrame print={print} state="error" error="forbidden">
        <PaperMessage print={print} title="You do not have permission to view reports." />
      </ReportFrame>
    );
  }

  const toolbar = rendering ? null : (
    <>
      <Link to="/lexistar/reports" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
        <FaArrowLeft size={10} /> Reports
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        {print ? (
          <>
            <button type="button" className={toolbarButton} onClick={() => setPrint(false)}>
              <FaDesktop size={11} /> Screen view
            </button>
            <button type="button" className={toolbarButton} onClick={() => window.print()}>
              <FaPrint size={11} /> Print
            </button>
          </>
        ) : (
          <button type="button" className={toolbarButton} onClick={() => setPrint(true)}>
            <FaPrint size={11} /> Print layout
          </button>
        )}
        <ExportButtons
          reportKey="run_summary"
          parameters={{ run_id: runId }}
          formats={['pdf']}
          disabled={state.load !== 'ready'}
          filenameBase={`run-summary-${runId.slice(0, 8)}`}
          buttonClassName={toolbarPrimary}
        />
      </div>
    </>
  );

  return (
    <ReportFrame print={print} state={state.load} error={state.error} toolbar={toolbar}>
      {state.load === 'loading' ? (
        <PaperMessage print={print} title="Loading the run summary…" />
      ) : state.error || !state.data ? (
        <PaperMessage print={print} title={state.error || 'The run summary could not be loaded.'} />
      ) : (
        <RunDocument data={state.data} print={print} generatedAt={state.at} />
      )}
    </ReportFrame>
  );
}
